import { useAppSelector } from '@/store';
import { IndicatorType } from '@portal_components/components/WorkflowIndicator';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { RestTypes, useJbpQuery } from '@/hooks/useJbpQuery';
import { useDefinedParams } from '@/hooks/useDefinedParams';
import { useMemo } from 'react';
import { FormButton } from '@portal_components/components/FormButton';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { FormWorkflowView } from '@portal_components/formWorkflow/FormWorkflowView';
import { Title } from '@portal_components/components/Title';
import { AvailableJobType } from '../../../../../APIDocs/apiTypes';

type BookJobJobTypeFormValues = {
  jobType: string;
};

export const FormBookJobJobType = () => {
  const [mpan, serviceLine] = useDefinedParams(['mpan', 'serviceLine']);
  const navigate = useNavigate();
  const { site } = useAppSelector(state => state.jbp);

  const siteType = useMemo(() => {
    let initialSiteType = site?.siteType;
    if (['DNO', 'BNO'].includes(serviceLine)) {
      initialSiteType = serviceLine;
    } else if (site?.updatedSiteType != undefined) {
      initialSiteType = site?.updatedSiteType;
    }
    return initialSiteType;
  }, [site, serviceLine]);

  const { data: jobTypes, isLoading: isJobTypesLoading } = useJbpQuery<AvailableJobType[], any>(
    RestTypes.Get,
    undefined,
    [`job-types-${mpan}-${serviceLine}-${siteType}`],
    `jbp/${mpan}/jobTypes/${siteType}`,
    true,
    serviceLine
  );

  const handleSubmit = (values: BookJobJobTypeFormValues) => {
    const selectedJobTypeTitle = jobTypes?.find(jobType => jobType.JobTypeCode === values.jobType)?.CategoryTitle;
    if (!selectedJobTypeTitle) return;

    if (selectedJobTypeTitle.toLowerCase().indexOf('warrant') > -1) {
      navigate(`/bookJob/warrantSiteInfo/${mpan}/${serviceLine}/${values.jobType}`);
    } else {
      navigate(`/bookJob/siteInfo/${mpan}/${serviceLine}/${values.jobType}`);
    }
  };

  if (isJobTypesLoading || !jobTypes) {
    return <div>Loading Job Types...</div>;
  }

  return (
    <div className="ml-10 pb-4 mb-10">
      <Title className="mt-7 mb-5" text={`Pre Booking for: ${mpan}`} />
      <FormWorkflowView<BookJobJobTypeFormValues>
        onSubmit={handleSubmit}
        nodes={[
          {
            element: (
              <Title
                className="mt-7 mb-5 font-italic text-sm"
                text={`The current known site type for this booking is: ${site?.updatedSiteType}. If this is not correct please return to the previous screen to correct this before continuing with the booking.`}
              />
            ),
            classname: 'bg-elevation-1 mr-8',
            indicatorType: IndicatorType.Info
          },
          {
            name: 'jobType',
            element: (
              <div>
                <div className="w-full mb-4">
                  <Title text="Select the type of job to book" className="mb-5 mt-7" />
                  <FormRadioButtonGroup
                    title=""
                    name="jobType"
                    expandable
                    buttonClassName="w-[30em]"
                    buttons={jobTypes.map(jobType => ({ title: jobType.CategoryTitle, value: jobType.JobTypeCode, description: jobType.CategoryDescription }))}
                  />
                </div>
                <FormButton type="submit" icon primary>
                  Next
                </FormButton>
              </div>
            ),
            validationSchema: {
              jobType: Yup.string().required('Please select a job type')
            }
          }
        ]}
      />
    </div>
  );
};
